import React, { useEffect, useState } from 'react';

import constants from 'constants/constants';
import { isEmpty } from 'lodash';
import moment from 'moment-timezone';
import { useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { generatePath, useHistory, useLocation, useParams, useRouteMatch } from 'react-router-dom';
import { isStringHasRealContent, sortAlphabeticalArray } from 'utilitys/helpers/general';

import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';
import { useHashValue } from 'hooks/useHashValue';
import useUserHook from 'hooks/useUserHook';

import { useCommentsStore } from 'store/comments.store';
import { useCommentsEditorStatus } from 'store/commentsEditorStatus.store';
import { useProjectTreeStore } from 'store/projectContract.store';

import { useMutationCreateCard } from 'api/mutations/createCard.mutation';
import { useMutationCreateEditComment } from 'api/mutations/createEditComment.mutation';
import { useMutationDeleteExternalDocument } from 'api/mutations/deleteExternalDocument.mutation';
import { useMutationGetActivityOptions } from 'api/mutations/getActivityOptions.mutation';
import { useMutationGetWBSOptions } from 'api/mutations/getWBSOptions.mutation';
import { useQueryGetBoardInitLite } from 'api/queries/getBoardInitLite.query';

import OverlayWithSpinner from 'components/common/OverlayWithSpinner/overlayWithSpinner';
import { ConfirmChangesModal } from 'components/common/confirmChangesModal/confirmChangesModal';
import { LottieAnimation } from 'components/common/lottieAnimation/lottieAnimation';

import { IActiveTab } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainCard/Common/TabsSection/TabsSection';
import { useMainCardProvider } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainCard/MainCard.provider';
import { mapper } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainCard/MainCard.utils';
import { ProgramActivityCardModalDesktop } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainCard/ProgramActivityCardModal/ProgramActivityCardModal.desktop';
import { ProgramActivityCardModalMobile } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainCard/ProgramActivityCardModal/ProgramActivityCardModal.mobile';
import classes from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainCard/ProgramActivityCardModal/ProgramActivityCardModal.module.scss';
import { IFields } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainCard/ProgramActivityCardModal/ProgramActivityCardModal.types';
import { buildProgramActivityCardPayload } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainCard/ProgramActivityCardModal/ProgramActivityCardModal.utils';
import {
    activityIdMapper,
    assigneeMapper,
    versionMapper,
    wbsMapper,
} from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/helper';
import { Button } from 'components/DesignSystem/Button';
import { Modal } from 'components/DesignSystem/Modal';

interface IProgramActivityCardProps {
    saveCardCallback: (value?: number) => void;
    queryKeys: string[];
}

export const ProgramActivityCardModal = ({ saveCardCallback, queryKeys }: IProgramActivityCardProps) => {
    const { showModal, setShowModal, isMobile, api } = useMainCardProvider();
    const commentData = useCommentsStore((store) => store.commentData);
    const { resetCommentData, setCommentData, setCommentId } = useCommentsStore();
    const { resetCommentsEditorStatus } = useCommentsEditorStatus();
    const { confetti } = useUserHook();
    const queryClient = useQueryClient();

    const hashValue = useHashValue();
    const activeTabByHash = hashValue ? (hashValue as IActiveTab) : 'cardDetails';
    const { pathname, search } = useLocation();
    const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
    const [activeTab, setActiveTab] = useState<IActiveTab>(activeTabByHash);

    useEffect(() => {
        hashValue && setActiveTab(hashValue as IActiveTab);
    }, [hashValue]);

    const history = useHistory();
    const { path } = useRouteMatch();
    const params = useParams();
    const { project } = useProjectTreeStore();
    const { data: boardInit, isLoading: boardInitLoading } = useQueryGetBoardInitLite({ projectId: project?.id });
    const { mutate: mutateWBSOptions, isLoading: wbsOptionsOLoading } = useMutationGetWBSOptions();
    const { mutate: mutateActivityOptions, isLoading: activityOptionsLoading } = useMutationGetActivityOptions();
    const { mutate: mutateCreateCard, isLoading: createCardLoading } = useMutationCreateCard();
    const { mutate: createEditCommentMutate, isLoading: createEditCommentLoading } = useMutationCreateEditComment();
    const { mutate: deleteExternalDocumentMutate } = useMutationDeleteExternalDocument();

    const { handleSnackBar } = useCustomSnackBar();

    const isLoading =
        boardInitLoading ||
        wbsOptionsOLoading ||
        activityOptionsLoading ||
        createCardLoading ||
        createEditCommentLoading;

    const {
        register,
        control,
        handleSubmit,
        formState: { errors, dirtyFields },
        setValue,
        getValues,
        watch,
        reset,
    } = useForm<IFields>();

    // reset comments on first load - because 'handleModalClose' execute only on Cancel button click
    useEffect(() => {
        resetCommentsEditorStatus();
        resetCommentData();
    }, []);

    // ===============================================================================================================

    useEffect(() => {
        const data = api.getCard.data;
        if (boardInit && data) {
            // project
            const currentProject = boardInit.projectContractVersions.find((item) => item.id === data.project.id);

            // contract
            const contractOptions = currentProject?.contracts;
            const currentContract = contractOptions?.find((item) => item.id === data.contract.id);

            // versionName
            const versionOptions = currentContract?.metaDataResponse;
            const currentVersion = versionOptions?.find((item) => item.id === data.metaData.id);

            // WBS List Options
            if (currentVersion?.id) {
                mutateWBSOptions(
                    { versionId: currentVersion.id },
                    {
                        onSuccess: (res) => {
                            const wbsListOptions = sortAlphabeticalArray(res, 'name', 'asc').map(wbsMapper);
                            const currentWbsValue = wbsListOptions?.find(
                                (item) => item.id === data.relatedActivity.wbsId,
                            );
                            setValue('wbsValue', currentWbsValue || null);
                        },
                    },
                );
            }

            if (data.relatedActivity.wbsId) {
                mutateActivityOptions(
                    { wbsId: data.relatedActivity.wbsId },
                    {
                        onSuccess: (res) => {
                            const activityListOptions = sortAlphabeticalArray(res, 'name', 'asc').map(activityIdMapper);
                            const currentActivityId = activityListOptions?.find(
                                (item) => item.value === data.relatedActivity.activityId,
                            );

                            const currentActivityIdFromGetCardData = {
                                id: data.relatedActivity.id,
                                label: data.relatedActivity.activityId,
                                value: data.relatedActivity.activityId,
                            };

                            setValue('activityId', currentActivityId || currentActivityIdFromGetCardData);
                        },
                    },
                );
            }

            reset({
                id: !data.isVirtual ? data.id : null,
                project: currentProject ? mapper(currentProject) : null,
                contract: currentContract ? mapper(currentContract) : null,
                versionName: currentVersion ? versionMapper(currentVersion) : null,
                wbsValue: null,
                activityId: null,
                title: data.title,
                status: { ...data.status, originalStatus: data.originalStatus.name },
                originalStatus: data.originalStatus,
                details: data.details,
                mentionedList: data.mentionedList,
                tagList: data.tagList.map((item) => ({ label: item.name, value: item.name })),
                assignee: assigneeMapper(data.assignee),
                category: mapper(data.category),
                isImportant: data.isImportant,
                startDate: data.startDate,
                endDate: data.endDate,
                externalLinks: data.externalLinks,
                externalDocuments: data.externalDocuments,
                versionId: currentVersion?.id,
                remainingDurationInDays: data.remainingDurationInDays,
                submittedDurationComplete: data.submittedDurationComplete,

                relatedCardIds: null,
                type: 'PROGRAMME_ACTIVITY',

                // dates by activity id
                cardDates: {
                    baselinePlannedStartDate: moment(data.baselinePlannedStartDate).format(
                        constants.formats.date.default,
                    ),
                    baselinePlannedEndDate: moment(data.baselinePlannedEndDate).format(constants.formats.date.default),
                    plannedStartDate: moment(data.relatedActivity?.plannedStartDate).format(
                        constants.formats.date.default,
                    ),
                    plannedFinishDate: moment(data.relatedActivity?.plannedFinishDate).format(
                        constants.formats.date.default,
                    ),
                    calculatedStartDate: moment(data.relatedActivity?.calculatedStartDate).format(
                        constants.formats.date.default,
                    ),
                    calculatedFinishDate: moment(data.relatedActivity?.calculatedFinishDate).format(
                        constants.formats.date.default,
                    ),
                },
            });
        }
    }, [boardInit, api.getCard.data]);

    const deleteExternalDocument = (externalDocuments: IFields['externalDocuments']): boolean => {
        let deleteActionError: boolean = false;
        externalDocuments.forEach((externalDoc) => {
            if (externalDoc.delete !== undefined) {
                deleteExternalDocumentMutate(externalDoc, {
                    onError: (error) => {
                        DEFAULT_ERROR({ error, handleError: handleSnackBar });
                        deleteActionError = true;
                    },
                });
            }
        });
        return deleteActionError;
    };

    const getExternalDocumentsToSave = (externalDocuments: IFields['externalDocuments']) => {
        return externalDocuments.filter((document) => document.delete === undefined);
    };

    const handleCommentData = () => {
        if (!isStringHasRealContent(commentData.comment)) {
            createEditCommentMutate(commentData, {
                onSuccess: () => {
                    return Promise.all([
                        queryClient.invalidateQueries('getCard'),
                        queryClient.invalidateQueries('getCardComment'),
                    ]).then(() => {
                        resetCommentsEditorStatus();
                        setCommentData({
                            ...commentData,
                            cardId: api.getCard.data?.id,
                            isVirtual: api.getCard.data?.isVirtual,
                            comment: '',
                        });
                        setCommentId(false);
                    });
                },
                onError: (error) => {
                    DEFAULT_ERROR({ error, handleError: handleSnackBar });
                },
            });
        }
    };

    const handleSave = (data) => {
        const payload = buildProgramActivityCardPayload(data);

        let errorOnDeleteExternalDocsAction = false;
        if (payload.externalDocuments !== undefined) {
            errorOnDeleteExternalDocsAction = deleteExternalDocument(payload.externalDocuments);
            payload.externalDocuments = getExternalDocumentsToSave(payload.externalDocuments);
        }

        if (errorOnDeleteExternalDocsAction) return; // Do not continue save action after external document deletion failed.
        mutateCreateCard(payload, {
            onSuccess: (res) => {
                handleSnackBar('Card Successfully Updated', 'success');

                if (watch('id')) {
                    api.getCard.refetch();
                    reset(getValues());

                    handleCommentData();
                } else {
                    if (!isStringHasRealContent(commentData.comment)) {
                        createEditCommentMutate(
                            { ...commentData, cardId: res.id, isVirtual: false },
                            {
                                onSuccess: () => {
                                    resetCommentsEditorStatus();
                                    setCommentData({
                                        ...commentData,
                                        cardId: res.id,
                                        isVirtual: false,
                                        comment: '',
                                    });
                                    setCommentId(false);

                                    const link = `${generatePath(path, { ...params, cardId: res.id })}${search}`;
                                    history.push(link);
                                },
                                onError: (error) => {
                                    DEFAULT_ERROR({ error, handleError: handleSnackBar });
                                },
                            },
                        );
                    } else {
                        const link = `${generatePath(path, { ...params, cardId: res.id })}${search}`;
                        history.push(link);
                    }
                }
                saveCardCallback(res.id);
            },
            onError: (error) => {
                DEFAULT_ERROR({ error, handleError: handleSnackBar });
            },
            onSettled: () => {
                if (showConfirmModal) {
                    handleModalClose();
                }
            },
        });
    };

    const isStateEmpty = isEmpty(getValues());
    const isCardDirty = !isEmpty(dirtyFields) || !isStringHasRealContent(commentData.comment);

    const handleCardClose = () => {
        if (isCardDirty) {
            setShowConfirmModal(true);
        } else {
            handleModalClose();
        }
    };

    const handleConfirmModalSave = () => {
        handleSubmit(handleSave)();
    };

    //==================================== handleModalClose ============================================================
    const handleModalClose = () => {
        resetCommentsEditorStatus();
        resetCommentData();

        const pathWithoutParams = pathname.slice(0, pathname.lastIndexOf('/'));
        setShowConfirmModal(false);
        setShowModal(false);
        history.push({
            pathname: pathWithoutParams,
            search: search,
        });
    };
    //==================================================================================================================

    const status = watch('status.name');
    const originalStatus = watch('originalStatus.name');
    const condition =
        Boolean(confetti) && status === 'DONE' && originalStatus !== 'DONE' && Boolean(dirtyFields.status);
    const assignee = getValues('assignee');

    return (
        <Modal
            open={showModal}
            onClose={handleModalClose}
            width={'140rem'}
            title={'Program Activity'}
            actions={
                <>
                    <Button
                        size={'large'}
                        variant={'secondary'}
                        onClick={handleCardClose}
                        id={'cardModalCloseOrCancelButton'}
                        data-track-id={`button-close-card`}
                    >
                        Close
                    </Button>
                    <Button
                        type={'submit'}
                        size={'large'}
                        variant={'primary'}
                        id={'cardModalSaveButton'}
                        disabled={!isCardDirty}
                        data-track-id={`button-save-card`}
                        form={'program-activity-form'}
                    >
                        Save
                    </Button>
                </>
            }
        >
            <form
                id={'program-activity-form'}
                onSubmit={handleSubmit(handleSave)}
                className={classes.programActivityCardContainer}
            >
                {isLoading && <OverlayWithSpinner />}
                <LottieAnimation
                    condition={condition}
                    animationSrc={require('styles/assets/animations/confetti-done.lottie')}
                />
                {!isStateEmpty && !isMobile && (
                    <ProgramActivityCardModalDesktop
                        watch={watch}
                        control={control}
                        setValue={setValue}
                        data={api.getCard.data}
                        queryKeys={queryKeys}
                        register={register}
                        errors={errors}
                        boardInit={boardInit}
                        activeTab={activeTab}
                        setActiveTab={setActiveTab}
                        isCardDirty={isCardDirty}
                        handleCardClose={handleCardClose}
                    />
                )}
                {!isStateEmpty && isMobile && (
                    <ProgramActivityCardModalMobile
                        watch={watch}
                        control={control}
                        setValue={setValue}
                        data={api.getCard.data}
                        queryKeys={queryKeys}
                        register={register}
                        errors={errors}
                        boardInit={boardInit}
                        activeTab={activeTab}
                        setActiveTab={setActiveTab}
                        isCardDirty={isCardDirty}
                        handleCardClose={handleCardClose}
                        getValues={getValues}
                        assignee={assignee}
                    />
                )}
            </form>

            <ConfirmChangesModal
                showModal={showConfirmModal}
                onHideModal={() => setShowConfirmModal(false)}
                handleCloseWithoutSave={handleModalClose}
                handleSave={handleConfirmModalSave}
                isLoading={createCardLoading}
            >
                You made changes to this card. Would you like to save it?
            </ConfirmChangesModal>
        </Modal>
    );
};
